import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SignatureCanvas from 'react-signature-canvas';
import { 
  ChevronRight, ChevronLeft, Upload, Check, AlertCircle,
  User, Users, FileText, CreditCard, Shield, PenTool, X
} from 'lucide-react';
import { inscriptionsAPI, tarifsAPI, settingsAPI, filesAPI, cmsAPI } from '../../lib/api';
import { parseApiError } from '../../lib/apiErrors';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const STEPS = [
  { id: 'enfants', title: 'Informations Enfants', icon: User },
  { id: 'parents', title: 'Informations Parents', icon: Users },
  { id: 'documents', title: 'Documents', icon: FileText },
  { id: 'tarifs', title: 'Choix du Cours', icon: CreditCard },
  { id: 'autorisations', title: 'Autorisations', icon: Shield },
  { id: 'cgv', title: 'CGV & Signature', icon: PenTool },
];

const InscriptionFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const sigCanvas = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [tarifs, setTarifs] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [cmsContent, setCmsContent] = useState({});
  
  // État pour les documents individuels
  const [documentFiles, setDocumentFiles] = useState({
    identity: null,
    insurance: null,
    medical: null,
    photo: null
  });
  const [documentPreviews, setDocumentPreviews] = useState({
    identity: null,
    insurance: null,
    medical: null,
    photo: null
  });

  // Build initial form data, optionally pre-filled from location.state.prefill or current user
  const prefill = location.state?.prefill || {};
  const userPrefilledParent = user
    ? { nom: user.nom || '', prenom: user.prenom || '', telephone1: user.telephone || '', telephone2: '', email: user.email || '' }
    : { nom: '', prenom: '', telephone1: '', telephone2: '', email: '' };

  const initialEnfants = prefill.enfants && prefill.enfants.length
    ? prefill.enfants.map((e) => ({
        nom: e.nom || '', prenom: e.prenom || '', sexe: e.sexe || '',
        nationalite: e.nationalite || '', date_naissance: e.date_naissance || '',
        etablissement_actuel: e.etablissement_actuel || '', classe: e.classe || ''
      }))
    : [{ nom: '', prenom: '', sexe: '', nationalite: '', date_naissance: '', etablissement_actuel: '', classe: '' }];

  const [formData, setFormData] = useState({
    nombre_enfants: initialEnfants.length,
    enfants: initialEnfants,
    adresse: prefill.adresse || '',
    pere: prefill.pere || (user ? userPrefilledParent : { nom: '', prenom: '', telephone1: '', telephone2: '', email: '' }),
    mere: prefill.mere || { nom: '', prenom: '', telephone1: '', telephone2: '', email: '' },
    cours_choisis: [],
    documents_ids: [],
    allergies: '',
    personnes_autorisees: [{ nom: '', prenom: '', qualite: '', telephone: '' }],
    autorisation_photo: true,
    cgv_acceptees: false,
    // Nouveaux champs alignés sur le dossier officiel 2026-2027
    assurance_rc_attestee: false,    // "J'atteste que mon enfant est couvert par une assurance RC"
    autorisation_urgence: false,     // "J'autorise les responsables à prendre toutes dispositions"
    lieu_signature: '',
    date_signature: new Date().toISOString().split('T')[0],
    signature_image: '',
    signataire_urgence: 'pere',
    signataire_photo: 'pere',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [settingsRes, tarifsRes, cmsRes] = await Promise.all([
        settingsAPI.get(),
        tarifsAPI.list(),
        cmsAPI.getSections('form-enfant')
      ]);
      setSettings(settingsRes.data);
      setTarifs(tarifsRes.data.filter(t => !t.is_cpf));
      
      // Parse CMS content by section_key
      if (cmsRes.data && cmsRes.data.length > 0) {
        const content = {};
        cmsRes.data.forEach(section => {
          content[section.section_key] = section;
        });
        setCmsContent(content);
      }
      
      if (!settingsRes.data.inscription_open) {
        toast({ title: 'Inscriptions fermées', description: 'Les inscriptions sont actuellement fermées.', variant: 'destructive' });
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const updateEnfantCount = (count) => {
    const newEnfants = [...formData.enfants];
    while (newEnfants.length < count) {
      newEnfants.push({ nom: '', prenom: '', sexe: '', nationalite: '', date_naissance: '', etablissement_actuel: '', classe: '' });
    }
    while (newEnfants.length > count) {
      newEnfants.pop();
    }
    setFormData({ ...formData, nombre_enfants: count, enfants: newEnfants });
  };

  const updateEnfant = (index, field, value) => {
    const newEnfants = [...formData.enfants];
    newEnfants[index] = { ...newEnfants[index], [field]: value };
    setFormData({ ...formData, enfants: newEnfants });
  };

  const updateParent = (parent, field, value) => {
    setFormData({ ...formData, [parent]: { ...formData[parent], [field]: value } });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setLoading(true);
    try {
      const uploadPromises = files.map(file => filesAPI.upload(file));
      const results = await Promise.all(uploadPromises);
      const newFiles = results.map(res => ({ id: res.data.id, filename: res.data.filename }));
      setUploadedFiles([...uploadedFiles, ...newFiles]);
      setFormData({ ...formData, documents_ids: [...formData.documents_ids, ...newFiles.map(f => f.id)] });
      toast({ title: 'Fichiers téléchargés', description: `${files.length} fichier(s) ajouté(s)` });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors du téléchargement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Gestion des documents individuels avec aperçu
  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    
    setLoading(true);
    try {
      // Upload du fichier
      const res = await filesAPI.upload(file);
      
      // Mise à jour de l'état des fichiers
      setDocumentFiles(prev => ({ ...prev, [docType]: file }));
      
      // Créer l'aperçu pour les images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setDocumentPreviews(prev => ({ ...prev, [docType]: e.target.result }));
        };
        reader.readAsDataURL(file);
      }
      
      // Ajouter l'ID du document au formulaire
      setFormData(prev => ({
        ...prev,
        documents_ids: [...prev.documents_ids.filter(id => !id.startsWith(docType)), res.data.id]
      }));
      
      setUploadedFiles(prev => [...prev.filter(f => !f.docType || f.docType !== docType), { 
        id: res.data.id, 
        filename: res.data.filename,
        docType 
      }]);
      
      toast({ title: 'Document chargé', description: file.name });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Erreur lors du téléchargement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const removeDocument = (docType) => {
    setDocumentFiles(prev => ({ ...prev, [docType]: null }));
    setDocumentPreviews(prev => ({ ...prev, [docType]: null }));
    setUploadedFiles(prev => prev.filter(f => f.docType !== docType));
    setFormData(prev => ({
      ...prev,
      documents_ids: prev.documents_ids.filter(id => !uploadedFiles.find(f => f.docType === docType && f.id === id))
    }));
  };

  const toggleCours = (enfantIndex, tarif) => {
    const existing = formData.cours_choisis.find(
      c => c.groupe === tarif.groupe && c.horaire === `${tarif.jour} ${tarif.horaire}` && c.numero_enfant === enfantIndex + 1
    );
    
    if (existing) {
      setFormData({
        ...formData,
        cours_choisis: formData.cours_choisis.filter(c => c !== existing)
      });
    } else {
      const prix = enfantIndex === 0 ? tarif.prix_1er : enfantIndex === 1 ? tarif.prix_2e : tarif.prix_3e;
      setFormData({
        ...formData,
        cours_choisis: [...formData.cours_choisis, {
          groupe: tarif.groupe,
          horaire: `${tarif.jour} ${tarif.horaire}`,
          tarif: prix,
          numero_enfant: enfantIndex + 1
        }]
      });
    }
  };

  const addPersonneAutorisee = () => {
    setFormData({
      ...formData,
      personnes_autorisees: [...formData.personnes_autorisees, { nom: '', prenom: '', qualite: '', telephone: '' }]
    });
  };

  const updatePersonneAutorisee = (index, field, value) => {
    const newPersonnes = [...formData.personnes_autorisees];
    newPersonnes[index] = { ...newPersonnes[index], [field]: value };
    setFormData({ ...formData, personnes_autorisees: newPersonnes });
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setFormData({ ...formData, signature_image: '' });
  };

  const saveSignature = () => {
    if (sigCanvas.current) {
      const dataUrl = sigCanvas.current.toDataURL('image/png');
      setFormData({ ...formData, signature_image: dataUrl });
    }
  };

  const calculateTotal = () => {
    const coursTotal = formData.cours_choisis.reduce((sum, c) => sum + c.tarif, 0);
    return coursTotal + (settings.frais_inscription || 25);
  };

  // Decompose an amount into HT / TVA / TTC depending on settings.prices_are_ttc
  const breakdown = (amount) => {
    const value = parseFloat(amount) || 0;
    const rate = parseFloat(settings.vat_rate ?? 20);
    if (!value || !rate) return { ht: value, tva: 0, ttc: value };
    if (settings.prices_are_ttc === false) {
      // amount is HT
      const ttc = value * (1 + rate / 100);
      return { ht: value, tva: ttc - value, ttc };
    }
    // amount is TTC (default)
    const ht = value / (1 + rate / 100);
    return { ht, tva: value - ht, ttc: value };
  };

  const formatEUR = (v) => v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Enfants
        return formData.enfants.every(e => e.nom && e.prenom && e.sexe && e.date_naissance);
      case 1: // Parents
        return (formData.pere.nom && formData.pere.email) || (formData.mere.nom && formData.mere.email);
      case 2: // Documents
        return Boolean(documentFiles.identity && documentFiles.insurance);
      case 3: // Tarifs
        return formData.cours_choisis.length > 0;
      case 4: // Autorisations
        return formData.assurance_rc_attestee && formData.autorisation_urgence;
      case 5: // CGV
        return formData.cgv_acceptees && formData.lieu_signature && formData.signature_image;
      default:
        return true;
    }
  };

  // Returns a human-readable list of fields/files the user still needs to provide for the current step.
  const getMissingItems = () => {
    switch (currentStep) {
      case 0: {
        const missing = [];
        formData.enfants.forEach((e, idx) => {
          const lacks = [];
          if (!e.prenom) lacks.push('prénom');
          if (!e.nom) lacks.push('nom');
          if (!e.sexe) lacks.push('sexe');
          if (!e.date_naissance) lacks.push('date de naissance');
          if (lacks.length) missing.push(`Enfant ${idx + 1} : ${lacks.join(', ')}`);
        });
        return missing;
      }
      case 1: {
        const missing = [];
        const hasPere = formData.pere.nom && formData.pere.email;
        const hasMere = formData.mere.nom && formData.mere.email;
        if (!hasPere && !hasMere) missing.push("Renseignez au moins le nom et l'email d'un parent (père ou mère)");
        return missing;
      }
      case 2: {
        const missing = [];
        if (!documentFiles.identity) missing.push("Pièce d'identité (obligatoire)");
        if (!documentFiles.insurance) missing.push("Attestation d'assurance (obligatoire)");
        return missing;
      }
      case 3:
        return formData.cours_choisis.length === 0 ? ['Sélectionnez au moins un cours pour chaque enfant'] : [];
      case 4: {
        const missing = [];
        if (!formData.assurance_rc_attestee) missing.push("Cocher l'attestation d'assurance responsabilité civile");
        if (!formData.autorisation_urgence) missing.push("Cocher l'autorisation d'intervention médicale en cas d'urgence");
        return missing;
      }
      case 5: {
        const missing = [];
        if (!formData.cgv_acceptees) missing.push("Cocher l'acceptation des CGV");
        if (!formData.lieu_signature) missing.push('Indiquer le lieu de signature');
        if (!formData.signature_image) missing.push('Signer dans la zone prévue');
        return missing;
      }
      default:
        return [];
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      toast({ title: 'Formulaire incomplet', description: 'Veuillez remplir tous les champs obligatoires.', variant: 'destructive' });
      return;
    }

    // Sanitize payload : strip empty parent (no email) and empty authorized persons
    // to avoid Pydantic EmailStr/required-field validation errors.
    const isParentFilled = (p) => p && (p.nom || p.prenom || p.email) && p.email;
    const sanitized = {
      ...formData,
      pere: isParentFilled(formData.pere) ? formData.pere : null,
      mere: isParentFilled(formData.mere) ? formData.mere : null,
      personnes_autorisees: (formData.personnes_autorisees || []).filter(
        (p) => p && (p.nom || p.prenom || p.telephone || p.qualite)
      ),
    };

    setLoading(true);
    try {
      const response = await inscriptionsAPI.create(sanitized);
      toast({ title: 'Inscription envoyée', description: `Votre inscription ${response.data.numero} a été enregistrée.` });
      navigate(user ? '/espace-apprenant' : '/inscription/confirmation');
    } catch (error) {
      const description = parseApiError(error, 'Une erreur est survenue.');
      // eslint-disable-next-line no-console
      console.error('Inscription submit error:', error?.response?.data || error);
      toast({ title: 'Erreur', description, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    } else {
      const missing = getMissingItems();
      const description = missing.length
        ? `Éléments manquants : ${missing.join(' · ')}`
        : 'Veuillez remplir tous les champs obligatoires.';
      toast({
        title: 'Étape incomplète',
        description,
        variant: 'destructive',
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#285BBB] to-[#2C3C65]" data-testid="inscription-page">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-[#2C3C65]">Dossier d'Inscription {settings.inscription_year || '2026-2027'}</h1>
          <p className="text-[#6777A1]">The English Speaking Center - Centre d'Istres</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                    isActive ? 'bg-[#285BBB] text-white' :
                    isCompleted ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className={`hidden md:block ml-2 text-sm font-medium ${
                    isActive ? 'text-[#285BBB]' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                  {idx < STEPS.length - 1 && (
                    <div className={`w-8 md:w-16 h-1 mx-2 rounded ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Step 0: Enfants */}
              {currentStep === 0 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-xl font-bold text-[#2C3C65] mb-4">Informations des enfants</h2>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#2C3C65] mb-2">Nombre d'enfants</label>
                      <select
                        value={formData.nombre_enfants}
                        onChange={(e) => updateEnfantCount(parseInt(e.target.value))}
                        className="w-48 p-3 border rounded-lg"
                        data-testid="nombre-enfants"
                      >
                        <option value={1}>1 enfant</option>
                        <option value={2}>2 enfants</option>
                        <option value={3}>3 enfants</option>
                      </select>
                    </div>
                  </div>

                  {formData.enfants.map((enfant, idx) => (
                    <div key={idx} className="bg-[#F5F7FF] p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-[#285BBB] mb-4">{idx + 1}{idx === 0 ? 'er' : 'ème'} Enfant</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nom *</label>
                          <Input value={enfant.nom} onChange={(e) => updateEnfant(idx, 'nom', e.target.value)} data-testid={`enfant-${idx}-nom`} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#2C3C65] mb-1">Prénom *</label>
                          <Input value={enfant.prenom} onChange={(e) => updateEnfant(idx, 'prenom', e.target.value)} data-testid={`enfant-${idx}-prenom`} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#2C3C65] mb-1">Sexe *</label>
                          <select
                            value={enfant.sexe}
                            onChange={(e) => updateEnfant(idx, 'sexe', e.target.value)}
                            className="w-full p-3 border rounded-lg"
                            data-testid={`enfant-${idx}-sexe`}
                          >
                            <option value="">Sélectionnez</option>
                            <option value="Masculin">Masculin</option>
                            <option value="Féminin">Féminin</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nationalité</label>
                          <Input value={enfant.nationalite} onChange={(e) => updateEnfant(idx, 'nationalite', e.target.value)} data-testid={`enfant-${idx}-nationalite`} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#2C3C65] mb-1">Date de naissance *</label>
                          <Input type="date" value={enfant.date_naissance} onChange={(e) => updateEnfant(idx, 'date_naissance', e.target.value)} data-testid={`enfant-${idx}-date-naissance`} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#2C3C65] mb-1">Établissement actuel</label>
                          <Input value={enfant.etablissement_actuel} onChange={(e) => updateEnfant(idx, 'etablissement_actuel', e.target.value)} data-testid={`enfant-${idx}-etablissement`} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#2C3C65] mb-1">Classe</label>
                          <Input value={enfant.classe} onChange={(e) => updateEnfant(idx, 'classe', e.target.value)} data-testid={`enfant-${idx}-classe`} />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Adresse *</label>
                    <Textarea 
                      value={formData.adresse} 
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} 
                      rows={2}
                      data-testid="adresse"
                    />
                  </div>
                </div>
              )}

              {/* Step 1: Parents */}
              {currentStep === 1 && (
                <div className="space-y-8">
                  <h2 className="text-xl font-bold text-[#2C3C65]">Informations des parents</h2>
                  
                  <div className="bg-[#F5F7FF] p-6 rounded-xl">
                    <h3 className="text-lg font-semibold text-[#285BBB] mb-4">Père</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nom</label>
                        <Input value={formData.pere.nom} onChange={(e) => updateParent('pere', 'nom', e.target.value)} data-testid="pere-nom" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Prénom</label>
                        <Input value={formData.pere.prenom} onChange={(e) => updateParent('pere', 'prenom', e.target.value)} data-testid="pere-prenom" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Téléphone 1</label>
                        <Input value={formData.pere.telephone1} onChange={(e) => updateParent('pere', 'telephone1', e.target.value)} data-testid="pere-tel1" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Téléphone 2</label>
                        <Input value={formData.pere.telephone2} onChange={(e) => updateParent('pere', 'telephone2', e.target.value)} data-testid="pere-tel2" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Email</label>
                        <Input type="email" value={formData.pere.email} onChange={(e) => updateParent('pere', 'email', e.target.value)} data-testid="pere-email" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F5F7FF] p-6 rounded-xl">
                    <h3 className="text-lg font-semibold text-[#285BBB] mb-4">Mère</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nom</label>
                        <Input value={formData.mere.nom} onChange={(e) => updateParent('mere', 'nom', e.target.value)} data-testid="mere-nom" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Prénom</label>
                        <Input value={formData.mere.prenom} onChange={(e) => updateParent('mere', 'prenom', e.target.value)} data-testid="mere-prenom" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Téléphone 1</label>
                        <Input value={formData.mere.telephone1} onChange={(e) => updateParent('mere', 'telephone1', e.target.value)} data-testid="mere-tel1" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Téléphone 2</label>
                        <Input value={formData.mere.telephone2} onChange={(e) => updateParent('mere', 'telephone2', e.target.value)} data-testid="mere-tel2" />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-[#2C3C65] mb-1">Email</label>
                        <Input type="email" value={formData.mere.email} onChange={(e) => updateParent('mere', 'email', e.target.value)} data-testid="mere-email" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Documents */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-[#2C3C65]">Documents requis</h2>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-yellow-800">Documents obligatoires</h4>
                        <p className="mt-1 text-sm text-yellow-700">
                          Vous devez fournir au minimum la <strong>pièce d'identité</strong> et l'<strong>attestation d'assurance responsabilité civile</strong> pour pouvoir passer à l'étape suivante. Le certificat médical et la photo sont optionnels mais recommandés.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Zones d'upload individuelles */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Pièce d'identité */}
                    <div className="space-y-2">
                      <label className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Pièce d'identité <span className="text-red-500">*</span>
                        </span>
                        {documentFiles.identity && (
                          <span className="flex items-center text-green-600 text-sm">
                            <Check className="h-4 w-4 mr-1" />
                            Chargé
                          </span>
                        )}
                      </label>
                      <p className="text-xs text-gray-500">Carte d'identité ou livret de famille</p>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload('identity', e.target.files[0])}
                        className="hidden"
                        id="doc-identity"
                      />
                      {!documentFiles.identity ? (
                        <label
                          htmlFor="doc-identity"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 hover:border-[#285BBB] transition-all"
                        >
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Cliquez pour sélectionner</span>
                        </label>
                      ) : (
                        <div className="relative border-2 border-green-300 bg-green-50 rounded-xl p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 shadow-sm border">
                              {documentPreviews.identity ? (
                                <img src={documentPreviews.identity} alt="Aperçu" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-red-50">
                                  <FileText className="h-8 w-8 text-red-500" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{documentFiles.identity.name}</p>
                              <p className="text-xs text-gray-500">{(documentFiles.identity.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button type="button" onClick={() => removeDocument('identity')} className="p-1 text-gray-400 hover:text-red-500">
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Attestation d'assurance */}
                    <div className="space-y-2">
                      <label className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Attestation d'assurance <span className="text-red-500">*</span>
                        </span>
                        {documentFiles.insurance && (
                          <span className="flex items-center text-green-600 text-sm">
                            <Check className="h-4 w-4 mr-1" />
                            Chargé
                          </span>
                        )}
                      </label>
                      <p className="text-xs text-gray-500">Responsabilité civile en cours de validité</p>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload('insurance', e.target.files[0])}
                        className="hidden"
                        id="doc-insurance"
                      />
                      {!documentFiles.insurance ? (
                        <label
                          htmlFor="doc-insurance"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 hover:border-[#285BBB] transition-all"
                        >
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Cliquez pour sélectionner</span>
                        </label>
                      ) : (
                        <div className="relative border-2 border-green-300 bg-green-50 rounded-xl p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 shadow-sm border">
                              {documentPreviews.insurance ? (
                                <img src={documentPreviews.insurance} alt="Aperçu" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-red-50">
                                  <FileText className="h-8 w-8 text-red-500" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{documentFiles.insurance.name}</p>
                              <p className="text-xs text-gray-500">{(documentFiles.insurance.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button type="button" onClick={() => removeDocument('insurance')} className="p-1 text-gray-400 hover:text-red-500">
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Certificat médical (optionnel) */}
                    <div className="space-y-2">
                      <label className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Certificat médical
                        </span>
                        {documentFiles.medical && (
                          <span className="flex items-center text-green-600 text-sm">
                            <Check className="h-4 w-4 mr-1" />
                            Chargé
                          </span>
                        )}
                      </label>
                      <p className="text-xs text-gray-500">Si nécessaire (allergies, etc.)</p>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload('medical', e.target.files[0])}
                        className="hidden"
                        id="doc-medical"
                      />
                      {!documentFiles.medical ? (
                        <label
                          htmlFor="doc-medical"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 hover:border-[#285BBB] transition-all"
                        >
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Cliquez pour sélectionner</span>
                        </label>
                      ) : (
                        <div className="relative border-2 border-green-300 bg-green-50 rounded-xl p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 shadow-sm border">
                              {documentPreviews.medical ? (
                                <img src={documentPreviews.medical} alt="Aperçu" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-red-50">
                                  <FileText className="h-8 w-8 text-red-500" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{documentFiles.medical.name}</p>
                              <p className="text-xs text-gray-500">{(documentFiles.medical.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button type="button" onClick={() => removeDocument('medical')} className="p-1 text-gray-400 hover:text-red-500">
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Photo d'identité */}
                    <div className="space-y-2">
                      <label className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Photo d'identité
                        </span>
                        {documentFiles.photo && (
                          <span className="flex items-center text-green-600 text-sm">
                            <Check className="h-4 w-4 mr-1" />
                            Chargé
                          </span>
                        )}
                      </label>
                      <p className="text-xs text-gray-500">Pour la carte d'étudiant</p>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload('photo', e.target.files[0])}
                        className="hidden"
                        id="doc-photo"
                      />
                      {!documentFiles.photo ? (
                        <label
                          htmlFor="doc-photo"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 hover:border-[#285BBB] transition-all"
                        >
                          <Upload className="h-8 w-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">Cliquez pour sélectionner</span>
                        </label>
                      ) : (
                        <div className="relative border-2 border-green-300 bg-green-50 rounded-xl p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 shadow-sm border">
                              {documentPreviews.photo ? (
                                <img src={documentPreviews.photo} alt="Aperçu" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FileText className="h-8 w-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{documentFiles.photo.name}</p>
                              <p className="text-xs text-gray-500">{(documentFiles.photo.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button type="button" onClick={() => removeDocument('photo')} className="p-1 text-gray-400 hover:text-red-500">
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Résumé des fichiers */}
                  {Object.keys(documentFiles).filter(k => documentFiles[k]).length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <Check className="h-5 w-5" />
                        <span className="font-medium">
                          {Object.keys(documentFiles).filter(k => documentFiles[k]).length} document(s) chargé(s) sur 4
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Tarifs */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-[#2C3C65]">Choix du cours</h2>
                  <p className="text-[#6777A1]">Sélectionnez le cours pour chaque enfant. Les tarifs sont appliqués automatiquement.</p>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                    📅 <strong>Session 2026-2027</strong> : du <strong>16 septembre 2026 au 12 juin 2027</strong> · 29 semaines · {settings.prices_are_ttc === false ? 'Prix affichés HT' : 'Prix affichés TTC'} · TVA {settings.vat_rate ?? 20}%
                  </div>

                  {formData.enfants.map((enfant, enfantIdx) => (
                    <div key={enfantIdx} className="bg-[#F5F7FF] p-6 rounded-xl">
                      <h3 className="text-lg font-semibold text-[#285BBB] mb-4">
                        {enfant.prenom} {enfant.nom} ({enfantIdx === 0 ? '1er' : enfantIdx === 1 ? '2ème' : '3ème'} enfant)
                      </h3>
                      <div className="space-y-3">
                        {tarifs.map((tarif, idx) => {
                          const isSelected = formData.cours_choisis.some(
                            c => c.groupe === tarif.groupe && c.horaire === `${tarif.jour} ${tarif.horaire}` && c.numero_enfant === enfantIdx + 1
                          );
                          const prix = enfantIdx === 0 ? tarif.prix_1er : enfantIdx === 1 ? tarif.prix_2e : tarif.prix_3e;
                          const bd = breakdown(prix);
                          const isTtc = settings.prices_are_ttc !== false;
                          return (
                            <div
                              key={idx}
                              onClick={() => toggleCours(enfantIdx, tarif)}
                              className={`p-4 rounded-lg cursor-pointer transition-all border-2 ${
                                isSelected 
                                  ? 'border-[#285BBB] bg-white shadow-md' 
                                  : 'border-transparent bg-white hover:border-gray-300'
                              }`}
                              data-testid={`tarif-${enfantIdx}-${idx}`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-semibold text-[#2C3C65]">{tarif.groupe}</p>
                                  <p className="text-sm text-[#6777A1]">{tarif.age_range} - {tarif.niveau}</p>
                                  <p className="text-sm text-[#6777A1]">{tarif.jour} {tarif.horaire}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-[#285BBB]">{prix}€ <span className="text-xs font-medium text-[#6777A1]">{isTtc ? 'TTC' : 'HT'}</span></p>
                                  {!!prix && (
                                    <p className="text-[11px] text-[#6777A1] leading-tight">
                                      {isTtc ? `HT ${formatEUR(bd.ht)}` : `TTC ${formatEUR(bd.ttc)}`} · TVA {formatEUR(bd.tva)}
                                    </p>
                                  )}
                                  {isSelected && <Check className="h-6 w-6 text-green-500 ml-auto mt-1" />}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  <div className="bg-[#2C3C65] text-white p-6 rounded-xl" data-testid="total-breakdown">
                    {(() => {
                      const total = calculateTotal();
                      const totalBd = breakdown(total);
                      const isTtc = settings.prices_are_ttc !== false;
                      return (
                        <>
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-lg font-bold">Total à régler</p>
                              <p className="text-xs text-blue-200">Cours + frais d'inscription ({settings.frais_inscription || 25}€)</p>
                            </div>
                            <p className="text-3xl font-bold">{formatEUR(isTtc ? totalBd.ttc : totalBd.ht)} {isTtc ? 'TTC' : 'HT'}</p>
                          </div>
                          <div className="border-t border-white/20 pt-3 grid grid-cols-3 gap-3 text-sm">
                            <div className="text-center">
                              <p className="text-xs text-blue-200 uppercase">Montant HT</p>
                              <p className="font-bold">{formatEUR(totalBd.ht)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-amber-200 uppercase">TVA ({settings.vat_rate ?? 20}%)</p>
                              <p className="font-bold text-amber-100">{formatEUR(totalBd.tva)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-blue-200 uppercase">Montant TTC</p>
                              <p className="font-bold">{formatEUR(totalBd.ttc)}</p>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Step 4: Autorisations */}
              {currentStep === 4 && (
                <div className="space-y-8">
                  <h2 className="text-xl font-bold text-[#2C3C65]">Autorisations</h2>

                  <div className="bg-[#F5F7FF] p-6 rounded-xl">
                    <h3 className="text-lg font-semibold text-[#285BBB] mb-4">Autorisation en cas d'urgence</h3>
                    <p className="text-sm text-[#6777A1] mb-4">
                      En cas d'accident, les parents sont contactés pour une prise en charge de l'élève. 
                      En cas d'urgence, le centre prendra l'initiative d'appeler les secours.
                    </p>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[#2C3C65] mb-1">Allergies ou informations médicales</label>
                      <Textarea
                        value={formData.allergies}
                        onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                        placeholder="Allergies médicamenteuses ou alimentaires..."
                        data-testid="allergies"
                      />
                    </div>

                    {/* Attestation assurance RC + autorisation d'urgence (dossier officiel 2026-2027) */}
                    <div className="space-y-3 mb-4 p-4 bg-white border border-blue-100 rounded-lg">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.assurance_rc_attestee}
                          onChange={(e) => setFormData({ ...formData, assurance_rc_attestee: e.target.checked })}
                          className="mt-0.5"
                          data-testid="assurance-rc"
                        />
                        <span className="text-sm text-[#2C3C65]">
                          J'atteste que mon enfant est couvert par une <strong>assurance responsabilité civile et individuelle</strong>.
                        </span>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.autorisation_urgence}
                          onChange={(e) => setFormData({ ...formData, autorisation_urgence: e.target.checked })}
                          className="mt-0.5"
                          data-testid="autorisation-urgence"
                        />
                        <span className="text-sm text-[#2C3C65]">
                          J'autorise les responsables à prendre toutes les dispositions nécessaires en cas d'accident ou problème de santé/sécurité de mon enfant, et à faire procéder aux interventions nécessaires en cas d'urgence.
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-2">Je soussigné(e) (le parent)</label>
                      <div className="flex space-x-4">
                        {['pere', 'mere', 'responsable'].map(type => (
                          <label key={type} className="flex items-center">
                            <input
                              type="radio"
                              name="signataire_urgence"
                              value={type}
                              checked={formData.signataire_urgence === type}
                              onChange={(e) => setFormData({ ...formData, signataire_urgence: e.target.value })}
                              className="mr-2"
                            />
                            {type === 'pere' ? 'Père' : type === 'mere' ? 'Mère' : 'Responsable légal'}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#F5F7FF] p-6 rounded-xl">
                    <h3 className="text-lg font-semibold text-[#285BBB] mb-2">Personnes autorisées à récupérer l'enfant</h3>
                    <p className="text-xs text-[#6777A1] mb-4 italic">
                      N.B. : ces personnes pourront être amenées à présenter leur pièce d'identité. Vous vous engagez à les présenter à l'enseignant et à signaler toute modification.
                    </p>
                    {formData.personnes_autorisees.map((personne, idx) => (
                      <div key={idx} className="grid md:grid-cols-4 gap-4 mb-4">
                        <Input placeholder="Nom" value={personne.nom} onChange={(e) => updatePersonneAutorisee(idx, 'nom', e.target.value)} />
                        <Input placeholder="Prénom" value={personne.prenom} onChange={(e) => updatePersonneAutorisee(idx, 'prenom', e.target.value)} />
                        <Input placeholder="Qualité" value={personne.qualite} onChange={(e) => updatePersonneAutorisee(idx, 'qualite', e.target.value)} />
                        <Input placeholder="Téléphone" value={personne.telephone} onChange={(e) => updatePersonneAutorisee(idx, 'telephone', e.target.value)} />
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={addPersonneAutorisee} className="mt-2">
                      + Ajouter une personne
                    </Button>
                  </div>

                  <div className="bg-[#F5F7FF] p-6 rounded-xl">
                    <h3 className="text-lg font-semibold text-[#285BBB] mb-4">Autorisation photo</h3>
                    <p className="text-sm text-[#6777A1] mb-4">
                      À l'occasion des activités dans le centre, il se peut que votre enfant soit photographié 
                      et que nous souhaitions faire figurer cette photo sur notre site web.
                    </p>
                    <div className="flex space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={formData.autorisation_photo === true}
                          onChange={() => setFormData({ ...formData, autorisation_photo: true })}
                          className="mr-2"
                        />
                        J'autorise la publication
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={formData.autorisation_photo === false}
                          onChange={() => setFormData({ ...formData, autorisation_photo: false })}
                          className="mr-2"
                        />
                        Je n'autorise pas
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: CGV & Signature */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <h2 className="text-xl font-bold text-[#2C3C65]">Conditions Générales de Vente</h2>
                  
                  <div className="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto text-sm text-gray-600">
                    <h4 className="font-semibold mb-2">1 - Objet</h4>
                    <p className="mb-3">Les présentes conditions générales de vente régissent les relations contractuelles entre The English Speaking Center et le client.</p>
                    
                    <h4 className="font-semibold mb-2">2 - Projet pédagogique</h4>
                    <p className="mb-3">Le projet pédagogique de The English Speaking Center est de permettre à chacun de participer à une activité en langue anglaise.</p>
                    
                    <h4 className="font-semibold mb-2">3 - Prix</h4>
                    <p className="mb-3">Les tarifs sont indiqués sur les brochures tarifaires. Les prix sont libellés en euros et calculés hors taxes.</p>
                    
                    <h4 className="font-semibold mb-2">4 - Paiements</h4>
                    <p className="mb-3">Le paiement est exigible dans sa totalité le jour de l'inscription.</p>
                    
                    <h4 className="font-semibold mb-2">5 - Résiliation</h4>
                    <p>En s'inscrivant à un atelier, les participants s'engagent pour la saison entière.</p>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.cgv_acceptees}
                      onChange={(e) => setFormData({ ...formData, cgv_acceptees: e.target.checked })}
                      className="mt-1 h-5 w-5 text-[#285BBB]"
                      data-testid="cgv-checkbox"
                    />
                    <label className="text-sm text-[#2C3C65]">
                      J'ai lu et j'accepte les conditions générales de vente *
                    </label>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-1">Fait à *</label>
                      <Input
                        value={formData.lieu_signature}
                        onChange={(e) => setFormData({ ...formData, lieu_signature: e.target.value })}
                        placeholder="Ville"
                        data-testid="lieu-signature"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2C3C65] mb-1">Le</label>
                      <Input
                        type="date"
                        value={formData.date_signature}
                        onChange={(e) => setFormData({ ...formData, date_signature: e.target.value })}
                        data-testid="date-signature"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-2">Signature *</label>
                    <div className="border-2 border-[#285BBB] rounded-lg overflow-hidden bg-white">
                      <SignatureCanvas
                        ref={sigCanvas}
                        canvasProps={{ className: 'w-full h-40' }}
                        onEnd={saveSignature}
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={clearSignature} className="mt-2">
                      <X className="h-4 w-4 mr-2" /> Effacer la signature
                    </Button>
                  </div>

                  <div className="bg-[#285BBB] text-white p-6 rounded-xl">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xl font-semibold">Total à régler</p>
                        <p className="text-sm text-blue-200">Inscription + Cours</p>
                      </div>
                      <p className="text-4xl font-bold">{calculateTotal()}€</p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center"
              data-testid="prev-btn"
            >
              <ChevronLeft className="h-5 w-5 mr-2" />
              Précédent
            </Button>
            <Button
              type="button"
              onClick={nextStep}
              disabled={loading || !validateStep()}
              className="flex items-center bg-[#285BBB] hover:bg-[#2C3C65] text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="next-btn"
              title={validateStep() ? '' : `Éléments manquants : ${getMissingItems().join(' · ')}`}
            >
              {loading ? 'Envoi...' : currentStep === STEPS.length - 1 ? "Soumettre l'inscription" : 'Suivant'}
              {currentStep < STEPS.length - 1 && <ChevronRight className="h-5 w-5 ml-2" />}
            </Button>
          </div>

          {/* Hint live des éléments manquants pour l'étape courante */}
          {!validateStep() && getMissingItems().length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="step-missing-hint">
              <p className="font-medium mb-1">À compléter avant de passer à l'étape suivante :</p>
              <ul className="list-disc list-inside space-y-0.5">
                {getMissingItems().map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InscriptionFormPage;
